import { useState, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type PaginationState,
} from '@tanstack/react-table';
import { ArrowUpDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';

/** Props do componente DataTable genérico */
export interface DataTableProps<T> {
  columns: ColumnDef<T, any>[];
  data: T[];
  isLoading?: boolean;
  searchPlaceholder?: string;
  searchColumn?: string;
  pageSize?: number;
  /** Se false, oculta a barra de busca da tabela (quando a página já filtra/busca acima). Predefinição: true */
  showToolbar?: boolean;
  /** Se false, oculta a paginação interna do DataTable. Predefinição: true */
  showPagination?: boolean;
  /** Se true, mantém a altura fixa com rolagem interna (estilo clássico). Padrão: false (rolagem fluida de página) */
  fixedHeight?: boolean;
  /** Elemento JSX personalizado para renderizar no lado direito da barra de ferramentas */
  toolbarActions?: React.ReactNode;
  /** Paginação orientada pelo servidor (quando os dados já vêm fatiados do backend) */
  serverPagination?: {
    pageIndex: number;
    pageCount: number;
    totalItems?: number;
    onPageChange: (page: number) => void;
    pageSizeOptions?: number[];
    onPageSizeChange?: (size: number) => void;
  };
}

export function DataTable<T>({
  columns,
  data,
  isLoading = false,
  searchPlaceholder = 'Buscar...',
  searchColumn: _searchColumn,
  pageSize = 10,
  showToolbar = true,
  showPagination = true,
  fixedHeight = true,
  toolbarActions,
  serverPagination,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize });

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter, pagination },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    globalFilterFn: (row, columnId, filterValue) => {
      const safeValue = String(filterValue).toLowerCase().trim();
      if (!safeValue) return true;

      const original = row.original;
      if (!original) return false;

      const checkValue = (val: any): boolean => {
        if (val == null) return false;
        if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
          return String(val).toLowerCase().includes(safeValue);
        }
        if (Array.isArray(val)) {
          return val.some(item => checkValue(item));
        }
        if (typeof val === 'object') {
          const proto = Object.getPrototypeOf(val);
          if (proto === null || proto === Object.prototype) {
            return Object.values(val).some(item => checkValue(item));
          }
        }
        return false;
      };

      return checkValue(original);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  useEffect(() => {
    setPagination((prev) => (prev.pageSize !== pageSize ? { ...prev, pageSize } : prev));
  }, [pageSize]);

  return (
    <div className={`data-table-wrapper ${!fixedHeight ? 'data-table-fluid' : ''}`}>
      {showToolbar && (
        <div className="dt-toolbar">
          <div className="dt-search">
            <Search size={16} className="dt-search-icon" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="dt-search-input"
            />
          </div>
          {toolbarActions && <div className="dt-toolbar-actions" style={{ marginLeft: 'auto' }}>{toolbarActions}</div>}
        </div>
      )}

      {/* Table */}
      <div className="dt-container">
        <table className="dt-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className={header.column.getCanSort() ? 'dt-th-sortable' : ''}
                  >
                    <div className="dt-th-content">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <ArrowUpDown size={14} className="dt-sort-icon" />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="dt-loading">
                  <div className="dt-spinner"></div>
                  Carregando...
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="dt-empty">
                  Nenhum registro encontrado
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="dt-row">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && (
        serverPagination ? (
          <PaginationFooter
            pageIndex={serverPagination.pageIndex}
            pageCount={serverPagination.pageCount}
            totalItems={serverPagination.totalItems}
            pageSize={pageSize}
            onPageChange={serverPagination.onPageChange}
            pageSizeOptions={serverPagination.pageSizeOptions}
            onPageSizeChange={serverPagination.onPageSizeChange}
          />
        ) : (
          <PaginationFooter
            pageIndex={table.getState().pagination.pageIndex}
            pageCount={table.getPageCount()}
            canPreviousPage={table.getCanPreviousPage()}
            canNextPage={table.getCanNextPage()}
            onPreviousPage={() => table.previousPage()}
            onNextPage={() => table.nextPage()}
          />
        )
      )}
    </div>
  );
}

export interface PaginationFooterProps {
  pageIndex?: number;
  pageSize?: number;
  totalItems?: number;
  pageCount?: number;
  onPageChange?: (page: number) => void;
  canPreviousPage?: boolean;
  canNextPage?: boolean;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
  className?: string;
  style?: React.CSSProperties;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
}

export function PaginationFooter({
  pageIndex = 0,
  pageSize = 10,
  totalItems,
  pageCount: explicitPageCount,
  onPageChange,
  canPreviousPage,
  canNextPage,
  onPreviousPage,
  onNextPage,
  className = '',
  style,
  pageSizeOptions,
  onPageSizeChange,
}: PaginationFooterProps) {
  const pageCount = explicitPageCount !== undefined
    ? explicitPageCount
    : (totalItems !== undefined ? Math.ceil(totalItems / pageSize) : 0);

  const isPrevDisabled = canPreviousPage !== undefined ? !canPreviousPage : pageIndex <= 0;
  const isNextDisabled = canNextPage !== undefined ? !canNextPage : (pageCount <= 0 || pageIndex >= pageCount - 1);

  const handlePrev = () => {
    if (isPrevDisabled) return;
    if (onPreviousPage) onPreviousPage();
    else if (onPageChange) onPageChange(pageIndex - 1);
  };

  const handleNext = () => {
    if (isNextDisabled) return;
    if (onNextPage) onNextPage();
    else if (onPageChange) onPageChange(pageIndex + 1);
  };

  return (
    <div className={`dt-pagination ${className}`} style={{ marginTop: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span className="dt-page-info">
          {totalItems !== undefined
            ? `Total: ${totalItems} registro(s) · Página ${pageCount > 0 ? pageIndex + 1 : 0} de ${pageCount}`
            : `Página ${pageCount > 0 ? pageIndex + 1 : 0} de ${pageCount}`}
        </span>

        {pageSizeOptions && onPageSizeChange && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', color: '#64748b' }}>
            <span>Itens por página:</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              style={{
                padding: '2px 6px',
                borderRadius: 6,
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                color: '#0f172a',
                fontSize: '.78rem',
                cursor: 'pointer',
              }}
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="dt-page-buttons">
        <button
          type="button"
          onClick={handlePrev}
          disabled={isPrevDisabled}
          className="dt-page-btn"
        >
          <ChevronLeft size={16} /> Anterior
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={isNextDisabled}
          className="dt-page-btn"
        >
          Próximo <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
